import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/cities-we-serve")({
  head: () => ({
    meta: [
      ...routeMeta("/cities-we-serve"),
      { title: "Central Florida CPA Service Areas | CCOB & Company" },
      { name: "description", content: "Local CPA services for businesses and families in Winter Garden, Ocoee, Windermere, Clermont, Apopka, Oakland, Gotha, and Orlando." },
      { property: "og:title", content: "Cities We Serve — CCOB And Company PA" },
      { property: "og:description", content: "Trusted CPA services across Central Florida." },
    ],
    links: canonicalLinks("/cities-we-serve"),
  }),
  component: CitiesPage,
});

const cities = [
  { name: "Winter Garden", desc: "Our home base for 30+ years.", miles: "0 mi", featured: true },
  { name: "Ocoee", desc: "Bookkeeping, payroll & tax planning.", miles: "5 mi" },
  { name: "Apopka", desc: "Clean books for growing businesses.", miles: "12 mi" },
  { name: "Windermere", desc: "Estate, trust & high-net-worth planning.", miles: "8 mi" },
  { name: "Clermont", desc: "Tax prep & S-Corp planning.", miles: "10 mi" },
  { name: "Oakland", desc: "Boutique, direct CPA service.", miles: "3 mi" },
  { name: "Gotha", desc: "Accounting & tax for owners.", miles: "6 mi" },
  { name: "Orlando", desc: "Year-round CPA support.", miles: "15 mi" },
];

function CitiesPage() {
  const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
  return (
    <>
      <PageHero
        eyebrow="Service Area"
        title="A Trusted CPA for Central Florida"
        subtitle="Based in Winter Garden — proudly serving individuals and businesses across the region."
        image={img.heroCities}
      />
      <section className="py-24 bg-secondary/30">
        <div className="container-x">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Where We Work</div>
            <h2 className="font-display text-4xl text-primary">Eight Communities, One Trusted Firm</h2>
            <div className="gold-line mt-5 mx-auto" />
            <p className="mt-5 text-muted-foreground">Local relationships, statewide expertise — within a short drive of our Winter Garden office.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map((c, i) => (
              <motion.div
                key={c.name}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className={`group relative overflow-hidden rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 ${
                  c.featured
                    ? "lg:col-span-2 lg:row-span-1 bg-primary text-primary-foreground shadow-xl"
                    : "bg-card border border-border hover:border-gold hover:shadow-lg"
                }`}
              >
                <div className={`absolute top-0 left-0 h-1 w-full ${c.featured ? "bg-gold" : "bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500"}`} />

                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-11 w-11 rounded-full grid place-items-center ${c.featured ? "bg-gold text-gold-foreground" : "bg-primary text-primary-foreground group-hover:bg-gold group-hover:text-gold-foreground transition-colors"}`}>
                      <MapPin className="h-5 w-5" strokeWidth={1.8} />
                    </div>
                    <div>
                      <h3 className={`font-display text-xl ${c.featured ? "text-primary-foreground" : "text-primary"}`}>{c.name}, FL</h3>
                      <div className={`text-[11px] uppercase tracking-widest mt-1 ${c.featured ? "text-gold" : "text-muted-foreground"}`}>{c.miles} from office</div>
                    </div>
                  </div>
                  {c.featured && (
                    <span className="text-[10px] uppercase tracking-widest font-medium bg-gold text-gold-foreground px-2.5 py-1 rounded-full">Headquarters</span>
                  )}
                </div>

                <p className={`mt-5 text-sm leading-relaxed ${c.featured ? "opacity-90" : "text-muted-foreground"}`}>{c.desc}</p>

                <Link
                  to="/contact"
                  hash="consultation"
                  className={`mt-6 inline-flex items-center gap-2 text-sm font-medium transition-colors ${c.featured ? "text-gold hover:text-primary-foreground" : "text-primary group-hover:text-gold"}`}
                >
                  Schedule a consultation
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-16 rounded-3xl overflow-hidden border border-border shadow-lg">
            <iframe
              title="CCOB And Company PA — Winter Garden, FL office location"
              src="https://www.google.com/maps?q=12200+W+Colonial+Dr+%23300B+Winter+Garden+FL+34787&output=embed"
              className="w-full h-[460px] border-0"
              loading="lazy"
            />
          </motion.div>
          <div className="mt-14 grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <h2 className="font-display text-3xl text-primary">Local meetings, flexible service</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">Clients can meet with us at our Winter Garden office, by phone, or virtually. Secure document exchange makes it practical to work with our CPA team from anywhere in the Orlando area while keeping personal attention at the center of the relationship.</p>
            </div>
            <div>
              <h2 className="font-display text-3xl text-primary">Support for businesses and families</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">Our Central Florida work includes bookkeeping, payroll, financial reporting, audits, tax preparation, IRS support, and year-round planning. We tailor the engagement to the client rather than limiting service by city boundary.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
